import React, { useState, useMemo, useEffect, useRef } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { useApp, formatTimerDisplay } from "../../context/AppContext";
import * as api from "../../lib/api";
import {
    LayoutDashboard,
    Plus,
    Ticket,
    CheckSquare,
    Settings,
    Users,
    Building2,
    Package,
    HardDrive,
    MonitorSmartphone,
    GitBranch,
    Server,
    Plug,
    FolderTree,
    Eye,
    Search,
    X,
    ArrowLeft,
    LogOut,
    Timer,
    Square,
    BookOpen,
    Zap,
    MessageCircle,
    Cloud,
    Briefcase,
    UserCog,
    Backpack,
    Printer,
    KeyRound,
} from "lucide-react";
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from "../ui/popover";

const getRoleLabel = (role) => {
    const roles = {
        admin: "Administrateur",
        agent: "Agent IT",
        coordinator: "Coordinateur",
        approver: "Approbateur",
        user: "Utilisateur",
    };
    return roles[role] || role;
};

const getInitials = (firstName, lastName) => {
    return `${firstName?.[0] || ""}${lastName?.[0] || ""}`.toUpperCase();
};

export function Sidebar() {
    const {
        currentUser,
        users,
        hasPermission,
        getDepartmentById,
        impersonating,
        realUser,
        startImpersonation,
        stopImpersonation,
        logout,
        timerState,
        stopTimer,
        isTeamSupervisor,
    } = useApp();

    const [impersonateOpen, setImpersonateOpen] = useState(false);
    const [impersonateSearch, setImpersonateSearch] = useState("");
    const [failedJobCount, setFailedJobCount] = useState(0);
    const [teamReqCount, setTeamReqCount] = useState(0);
    const navigate = useNavigate();
    const location = useLocation();
    const failedPollRef = useRef(null);

    // Poll for failed automation jobs count
    useEffect(() => {
        const fetchFailed = async () => {
            try {
                const data = await api.getAutomationFailedCount();
                setFailedJobCount(data.count || 0);
            } catch { /* silent */ }
        };
        if (hasPermission("automation")) {
            fetchFailed();
            failedPollRef.current = setInterval(fetchFailed, 60000);
            return () => clearInterval(failedPollRef.current);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentUser?.id, currentUser?.role, currentUser?.permissions]);

    // Poll for team request pending count
    useEffect(() => {
        if (!hasPermission("team_requests")) return;
        const fetchCount = async () => {
            try {
                const data = await api.getTeamRequestsPendingCount();
                setTeamReqCount(data.count || 0);
            } catch { /* silent */ }
        };
        fetchCount();
        const interval = setInterval(fetchCount, 60000);
        return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentUser?.id, currentUser?.role, currentUser?.permissions]);

    const isITStaff = () => {
        return currentUser?.role === "admin" || currentUser?.role === "agent" || currentUser?.role === "coordinator";
    };

    const filteredImpersonateUsers = useMemo(() => {
        if (!impersonateSearch.trim()) return [];
        const q = impersonateSearch.toLowerCase().trim();
        const selfId = currentUser?.id;
        return users
            .filter(
                (u) =>
                    u.id !== selfId &&
                    u.is_active !== false &&
                    u.role !== "admin" &&
                    (`${u.first_name} ${u.last_name}`.toLowerCase().includes(q) ||
                        u.email.toLowerCase().includes(q))
            )
            .slice(0, 8);
    }, [impersonateSearch, users, currentUser]);

    const handleStartImpersonate = (user) => {
        if (impersonating) stopImpersonation();
        startImpersonation(user);
        setImpersonateOpen(false);
        setImpersonateSearch("");
        navigate("/");
    };

    const navSections = [
        {
            title: "Principal",
            items: [
                { to: "/", icon: LayoutDashboard, label: "Tableau de bord" },
                { to: "/nouveau", icon: Plus, label: "Nouveau ticket" },
                { to: "/tickets", icon: Ticket, label: "Mes tickets" },
                { to: "/approbations", icon: CheckSquare, label: "Approbations" },
                ...((hasPermission("agent") || isTeamSupervisor) ? [{ to: "/team", icon: Users, label: "Mon équipe" }] : []),
                { to: "/base-de-connaissances", icon: BookOpen, label: "Base de connaissances" },
            ],
        },
        {
            title: "Administration",
            items: [
                { to: "/search", icon: Search, label: "Recherche", permission: "search" },
                { to: "/admin/inventory", icon: Backpack, label: "Inventaire", permission: "inventory", exclude: "/admin/inventory/printers" },
                { to: "/admin/inventory/printers", icon: Printer, label: "Imprimantes & consommables", permission: "printers", sub: true },
                { to: "/admin/tickets", icon: Ticket, label: "File de tickets", permission: "tickets_queue" },
                { to: "/admin/demandes-equipe", icon: UserCog, label: "Demandes d'équipe", permission: "team_requests", badge: teamReqCount > 0 ? teamReqCount : null },
                { to: "/admin/automatisation", icon: Zap, label: "Automatisation", permission: "automation", badge: failedJobCount > 0 ? failedJobCount : null },
                { to: "/admin/macros", icon: MessageCircle, label: "Macros", permission: "macros" },
                { to: "/admin/checklist-templates", icon: CheckSquare, label: "Templates checklists", permission: "checklists" },
                { to: "/admin/parametres", icon: Settings, label: "Paramètres", permission: "settings" },
                { to: "/admin/utilisateurs", icon: Users, label: "Utilisateurs", permission: "users" },
                { to: "/admin/departements", icon: Building2, label: "Départements", permission: "users" },
                { to: "/admin/positions", icon: Briefcase, label: "Postes", permission: "users" },
                { to: "/admin/acces", icon: KeyRound, label: "Rôles & accès", permission: "admin" },
            ],
        },
        {
            title: "Catalogues",
            items: [
                { to: "/admin/logiciels", icon: Package, label: "Logiciels", permission: "catalog" },
                { to: "/admin/ressources", icon: HardDrive, label: "Ressources", permission: "catalog" },
                { to: "/admin/materiel", icon: MonitorSmartphone, label: "Matériel", permission: "catalog" },
                { to: "/admin/postes", icon: Settings, label: "Modèles postes de travail", permission: "catalog" },
                { to: "/admin/workflows", icon: GitBranch, label: "Workflows", permission: "catalog" },
            ],
        },
        {
            title: "Configuration",
            items: [
                { to: "/admin/categories-tickets", icon: FolderTree, label: "Catégories tickets", permission: "catalog" },
                { to: "/admin/base-de-connaissances", icon: BookOpen, label: "Articles KB", permission: "kb" },
                { to: "/admin/integrations", icon: Plug, label: "Intégrations", permission: "settings" },
                { to: "/admin/entra", icon: Cloud, label: "Microsoft Entra ID", permission: "settings" },
                { to: "/admin/ldap", icon: Server, label: "LDAP / AD (mappings)", permission: "settings" },
            ],
        },
    ];

    return (
        <aside className="sidebar" data-testid="sidebar">
            {/* Logo */}
            <div className="sidebar-header">
                <NavLink to="/" className="sidebar-logo">
                    <div className="sidebar-logo-icon">T</div>
                    <span className="sidebar-logo-text">Ticketing</span>
                </NavLink>
            </div>

            {/* Impersonation banner */}
            {impersonating && (
                <div
                    className="mx-3 mb-2 px-3 py-2 bg-amber-500 rounded-lg text-white text-xs"
                    data-testid="impersonation-banner"
                >
                    <div className="flex items-center gap-2 mb-1">
                        <Eye className="w-3.5 h-3.5" />
                        <span className="font-semibold">Mode observation</span>
                    </div>
                    <p className="opacity-90 mb-2">
                        Vous voyez l'interface de {currentUser?.first_name} {currentUser?.last_name}
                    </p>
                    <button
                        onClick={() => { stopImpersonation(); navigate("/"); }}
                        className="flex items-center gap-1 px-2 py-1 bg-white/20 hover:bg-white/30 rounded text-xs font-medium transition-colors w-full justify-center"
                        data-testid="stop-impersonation-btn"
                    >
                        <ArrowLeft className="w-3 h-3" />
                        Revenir à {realUser?.first_name} {realUser?.last_name}
                    </button>
                </div>
            )}

            {/* Navigation */}
            <nav className="sidebar-nav sidebar-scroll">
                {navSections.map((section) => {
                    const visibleItems = section.items.filter((item) => {
                        if (!item.permission) return true;
                        return hasPermission(item.permission);
                    });

                    if (visibleItems.length === 0) return null;

                    return (
                        <div key={section.title} className="sidebar-section">
                            <div className="sidebar-section-title">{section.title}</div>
                            {visibleItems.map((item) => (
                                <NavLink
                                    key={item.to}
                                    to={item.to}
                                    className={({ isActive }) =>
                                        `sidebar-link ${item.sub ? "sidebar-sublink" : ""} ${isActive && !(item.exclude && location.pathname.startsWith(item.exclude)) ? "active" : ""}`
                                    }
                                    data-testid={`nav-${item.to.replace(/\//g, "-").slice(1) || "home"}`}
                                    end={item.to === "/"}
                                >
                                    <item.icon className="w-4 h-4" />
                                    <span>{item.label}</span>
                                    {item.badge && (
                                        <span className="ml-auto px-1.5 py-0.5 text-[9px] font-bold bg-red-500 text-white rounded-full" data-testid="automation-failed-badge">
                                            {item.badge}
                                        </span>
                                    )}
                                </NavLink>
                            ))}
                        </div>
                    );
                })}
            </nav>

            {/* Footer */}
            <div className="sidebar-footer">
                {/* Active timer indicator */}
                {timerState.isRunning && (
                    <NavLink
                        to={`/tickets/${timerState.ticketId}`}
                        className="mx-0 mb-2 flex items-center gap-2 px-3 py-2 bg-emerald-600 hover:bg-emerald-700 rounded-lg text-white text-xs transition-colors group"
                        data-testid="global-timer-indicator"
                    >
                        <Timer className="w-3.5 h-3.5 animate-pulse flex-shrink-0" />
                        <div className="flex-1 min-w-0">
                            <span className="font-mono font-semibold">{formatTimerDisplay(timerState.elapsedSeconds)}</span>
                            <span className="ml-1.5 opacity-80 truncate">#{timerState.ticketNumber}</span>
                        </div>
                        <button
                            onClick={async (e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                await stopTimer();
                            }}
                            className="p-1 bg-white/20 hover:bg-white/30 rounded transition-colors flex-shrink-0"
                            title="Arrêter le chrono"
                            data-testid="global-timer-stop-btn"
                        >
                            <Square className="w-3 h-3 fill-white" />
                        </button>
                    </NavLink>
                )}

                {/* Impersonate button - only for IT staff */}
                {isITStaff() && !impersonateOpen && (
                    <button
                        onClick={() => setImpersonateOpen(true)}
                        className="w-full flex items-center gap-2 px-3 py-2 mb-2 text-xs text-gray-400 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
                        data-testid="impersonate-btn"
                    >
                        <Eye className="w-4 h-4" />
                        <span>Voir en tant que...</span>
                    </button>
                )}

                {/* Impersonate search panel */}
                {impersonateOpen && (
                    <div className="mb-2 bg-gray-800 rounded-lg p-3 border border-gray-700" data-testid="impersonate-panel">
                        <div className="flex items-center justify-between mb-2">
                            <span className="text-xs font-semibold text-gray-300">Voir en tant que</span>
                            <button
                                onClick={() => { setImpersonateOpen(false); setImpersonateSearch(""); }}
                                className="text-gray-500 hover:text-gray-300"
                            >
                                <X className="w-3.5 h-3.5" />
                            </button>
                        </div>
                        <div className="relative">
                            <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-500" />
                            <input
                                type="text"
                                value={impersonateSearch}
                                onChange={(e) => setImpersonateSearch(e.target.value)}
                                placeholder="Nom, prénom ou email..."
                                className="w-full pl-8 pr-3 py-1.5 text-xs bg-gray-900 text-gray-200 border border-gray-700 rounded-md focus:outline-none focus:border-blue-500 placeholder-gray-600"
                                autoFocus
                                data-testid="impersonate-search"
                            />
                        </div>
                        {filteredImpersonateUsers.length > 0 && (
                            <div className="mt-2 max-h-48 overflow-y-auto space-y-0.5">
                                {filteredImpersonateUsers.map((u) => {
                                    const dept = getDepartmentById(u.department_id);
                                    return (
                                        <button
                                            key={u.id}
                                            onClick={() => handleStartImpersonate(u)}
                                            className="w-full flex items-center gap-2.5 px-2 py-2 text-left rounded-md hover:bg-gray-700 transition-colors"
                                            data-testid={`impersonate-user-${u.id}`}
                                        >
                                            <div className="w-7 h-7 rounded-full bg-gray-600 flex items-center justify-center text-[10px] font-bold text-gray-300 flex-shrink-0">
                                                {getInitials(u.first_name, u.last_name)}
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <div className="text-xs font-medium text-gray-200 truncate">
                                                    {u.first_name} {u.last_name}
                                                </div>
                                                <div className="text-[10px] text-gray-500 truncate">
                                                    {getRoleLabel(u.role)}{dept ? ` — ${dept.name}` : ""}
                                                </div>
                                            </div>
                                        </button>
                                    );
                                })}
                            </div>
                        )}
                        {impersonateSearch.trim() && filteredImpersonateUsers.length === 0 && (
                            <p className="text-[10px] text-gray-500 mt-2 text-center">Aucun résultat</p>
                        )}
                    </div>
                )}

                {/* Current User Display with Logout */}
                <Popover>
                    <PopoverTrigger asChild>
                        <button className="user-selector w-full" data-testid="user-display">
                            <div className="user-avatar">
                                {currentUser?.avatar_url ? (
                                    <img src={currentUser.avatar_url} alt="" />
                                ) : (
                                    getInitials(currentUser?.first_name, currentUser?.last_name)
                                )}
                            </div>
                            <div className="user-info">
                                <div className="user-name">
                                    {currentUser?.first_name} {currentUser?.last_name}
                                </div>
                                <div className="user-role">{getRoleLabel(currentUser?.role)}</div>
                            </div>
                        </button>
                    </PopoverTrigger>
                    <PopoverContent side="top" align="start" className="w-56 p-1.5" data-testid="user-popover">
                        <div className="px-2.5 py-2 border-b border-gray-100 mb-1">
                            <p className="text-sm font-medium text-gray-900 truncate">{currentUser?.first_name} {currentUser?.last_name}</p>
                            <p className="text-xs text-gray-500 truncate">{currentUser?.email}</p>
                        </div>
                        <button
                            onClick={async () => { await logout(); navigate("/login", { replace: true }); }}
                            className="w-full flex items-center gap-2.5 px-2.5 py-2 text-sm text-red-600 hover:bg-red-50 rounded-md transition-colors"
                            data-testid="logout-btn"
                        >
                            <LogOut className="w-4 h-4" />
                            <span>Se déconnecter</span>
                        </button>
                    </PopoverContent>
                </Popover>
            </div>
        </aside>
    );
}
